import {Injectable} from '@angular/core';
import {UserAlias} from '../../shared/constants';
import {StorageKeys} from '../../shared/models/storage-keys';
import {StringHelper} from '../helpers/strings.helper';


@Injectable()
export class LocalStorageService {
	readonly SELF_ON_BOARDING = 'SELF';
	private storeKeys: Array<any> = [];
	private store: any = {};
  readonly DEFAULT_ROUTE_TRACKER = {previousRoute: '/login', currentRoute: '/'};

	public get(key: string): any {
		if (key === StorageKeys.moniepointAgentId) {
			return this.getSessionData(key);
		}
		return localStorage.getItem(key);
	}

	public getAll(): any {
		const obj = {};
		this.storeKeys.forEach((i) => (obj[i] = localStorage.getItem(i)));
		return obj;
	}

	public save(key: string, value: any): boolean {
		localStorage.setItem(key, value);
		this.storeKeys.push(key);
		if (key === StorageKeys.moniepointAgentId) {
			this.saveSessionData(key, value);
		}
		return true;
	}

  getRouteTracker(){
    let routeTracker = this.get('route-tracker');
    try{ routeTracker = JSON.parse(routeTracker)}
    catch (error){routeTracker = this.DEFAULT_ROUTE_TRACKER}
    return routeTracker;
  }

	getBusinessAddress() {
		const address = this.get('business_address');
		if (!address) {
			return null;
		}
		return JSON.parse(address);
	}

	getUserRole() {
		return this.get('USER_ROLE');
	}

	saveUserRole(role: string) {
		if (!role) {
			return;
		}
		this.save('USER_ROLE', role);
	}

	saveUserTypePayload(params: {identityType: any, value:any}){
		this.save('UserTypePayload', JSON.stringify(params));
	}

	getUserTypePayload(){
		const data = this.get('UserTypePayload');
		return data ? JSON.parse(data) : {identityType: 'PHONENUMBER', value: null};
	}

	shouldCompleteSelfOnboardingKyc() {
		const canContinueKyc = this.get('SHOULD_COMPLETE_SELF_ONBOARDING_KYC');
		if (StringHelper.isNullOrEmptyOrUndefined(canContinueKyc)) {
			return false;
		}
		return JSON.parse(canContinueKyc);
	}

	getSaveTokenData() {
		const tokenData = this.get(StorageKeys.SAVED_TOKEN_DATA);
		return tokenData ? JSON.parse(tokenData) : {userId: null};
	}

	setCompleteSelfOnboardingKyc(state: boolean | null) {
		if (StringHelper.isNullOrEmptyOrUndefined(state)) {
			this.deleteValue(StorageKeys.should_complete_self_onboarding_kyc);
		}
		this.save(StorageKeys.should_complete_self_onboarding_kyc, state);
	}

	saveBusinessDetails(business) {
		const hasDefaultBrm = business?.hasDefaultBrm || false;
		this.save(StorageKeys.has_default_brm, JSON.stringify(hasDefaultBrm));
		if (business?.digitalOnboarded) {
			this.save(StorageKeys.on_boarding_mode, this.SELF_ON_BOARDING);
		}
		const businessAddress = {
			addressLineOne: business?.addressLineOne,
			businessPhone: business?.businessPhone,
			city: business?.city,
			localGovernmentId: business?.localGovernment?.id,
			stateId: business?.localGovernment?.state?.id
		};
		this.save(StorageKeys.business_address, JSON.stringify(businessAddress));
		this.save(StorageKeys.moniepointAgentId, business?.id);
		this.save(StorageKeys.selected_role, UserAlias.agent);
		this.save(StorageKeys.is_categorized_business, business?.categorized);
		this.save(StorageKeys.is_categorized_terminal, business?.terminalCategorized);
		this.save(StorageKeys.business_category, business?.businessCategory);
		this.save(StorageKeys.business_type, business?.businessType);
		this.save(StorageKeys.businessName, business?.businessName);
		this.save(StorageKeys.associated_business_types, business?.associatedBusinessTypes);
		this.save(StorageKeys.customer_business_type, business?.customerBusinessType);
		this.save(StorageKeys.store_front_image_reference, business?.storeFrontImageReference);
	}

	setSelectedRole(roles: string[]) {
		roles = roles || [];
		let userRole = roles.length ? roles[0] : null;
		this.setUserRole(userRole, false);
		const isAgent = roles.includes(UserAlias.agent);
		const isAgentManager = roles.includes(UserAlias.agentManager);
		const isStaffUser = roles.includes(UserAlias.staffUser);
		const isSupremeAggregator = roles.includes(UserAlias.supremeAggregator);
		const isSubBRM = roles.includes(UserAlias.subBRM);
		const isClusterManager = roles.includes(UserAlias.clusterManager);
		// const isStaffAdmin = roles.includes(UserAlias.staffAdmin);
		userRole = isAgentManager ? UserAlias.agentManager : userRole;
		userRole = isAgent && !isAgentManager ? UserAlias.agent : userRole;
		userRole = isStaffUser ? UserAlias.staffUser : userRole;
		userRole = isSupremeAggregator ? UserAlias.supremeAggregator : userRole;
		userRole = isSubBRM ? UserAlias.subBRM : userRole;
		userRole = isClusterManager ? UserAlias.clusterManager : userRole;
		// userRole = isStaffAdmin ? UserAlias.staffAdmin : userRole;
		this.setUserRole(userRole, isAgentManager || isAgent);
		return userRole;
	}

	handleBusinessesInResponse(res, businesses) {
		const businessData = {
			businesses,
			user: {
				firstName: res?.tokenData?.firstName,
				lastName: res?.tokenData?.lastName,
			},
		};
		sessionStorage.setItem('business-data', JSON.stringify(businessData));
		this.save('business-data', JSON.stringify(businessData));
	}

	getFullName() {
		const fistName = this.get('firstName') || '';
		const lastName = this.get('lastName') || '';
		return `${fistName} ${lastName}`;
	}

	deleteToken() {
		this.deleteValue(StorageKeys.token);
	}

	persistUserData(res, isStaticAgent = false) {
		const roles: any[] = res?.token?.roles;
		if (isStaticAgent) {
			roles.push(UserAlias.staticAgent);
		}
		this.saveAll(res.token);
		this.save('device_identifier', res?.encryptedDeviceId);
		this.save('authorities', JSON.stringify(res?.token?.authorities));
		this.save('roles', JSON.stringify(roles));
		this.save('aud', JSON.stringify(res?.token?.aud));
		this.save('scope', JSON.stringify(res?.token?.scope));
		this.save('expires_in', res?.token?.exp);
		this.save('browser', res?.browser);
		this.save('machine_os', res?.machineOS);
		this.save('access_token', res?.rawToken);
	}

	setSessionExpiration(val) {
		const expirationSeconds = val ? parseInt(val, 10) : 0;
		this.save('session_expiration', expirationSeconds);
	}

	hasAccessToken() {
		return !StringHelper.isNullOrEmpty(this.get('access_token'));
	}

	public saveAll(obj: any = {}): boolean {
		for (const x in obj) {
			if (obj.hasOwnProperty(x)) {
				localStorage.setItem(x, obj[x]);
				this.storeKeys.push(x);
			}
		}
		return true;
	}

	public deleteValue(key: any): boolean {
		localStorage.removeItem(key);
		return true;
	}

	public deleteAll(): boolean {
		localStorage.clear();
		return true;
	}

	public saveSessionData(key: string, value: any): boolean {
		sessionStorage.setItem(key, value);
		return true;
	}

	public getSessionData(key: string): any {
		const data = sessionStorage.getItem(key);
		if (data) {
			return data;
		}
		return localStorage.getItem(key);
	}

	private setUserRole(role, isDualUser) {
		this.save('selected_role', role);
		if (isDualUser) {
			this.save('dualUser', 'dualUser');
		}
	}

}
